import { memo, useId, useLayoutEffect, useMemo, useRef } from 'react'
import { Check } from 'lucide-react'
import { PrepackCard } from './PrepackCard'
import { bannerImageOf } from './prepackCardModel'

/**
 * Whole-tile template selection. Native radios keep the tile keyboard
 * accessible while the embedded card remains a non-interactive preview.
 * Memoized: each tile is a card with an artwork frame, re-rendered only when
 * its inputs change, not on every title keystroke (see LiveTemplatePicker).
 * The tiles follow the Configuration catalog order, including the Default card
 * at its saved slot. A hidden Default card is omitted unless it is selected; a
 * catalog without a fallback row gets a trailing Default card. A template that
 * allows AI pictures is marked "AI pictures", one known to have no picture
 * (the Default card, a builder template whose banner is not a picture) "No
 * image". Hand-authored artwork (the built-in Boosted card, hand-edited code)
 * may draw its own picture, so it is not marked.
 *
 * @param {object} props
 * @param {Array} props.templates - Available template descriptors
 * @param {string|null} props.value - Selected template ID (null = native Default)
 * @param {Function} props.onChange - Called with templateId (string | null)
 * @param {object} [props.pack] - Candidate pack to preview inside the tile
 * @param {boolean} [props.disabled] - Disabled state
 * @param {string} [props.filterQuery] - Shows only tiles whose name contains it;
 *   the selected tile stays, pinned first with a "Selected" marker
 * @param {'strip'|'grid'} [props.layout] - 'strip' (the default): full sample
 *   cards in a row that scrolls sideways; 'grid' (the schedule builder, issue
 *   683): compact mini-cards, several per row, cropped to the card's top, in
 *   a box that scrolls down
 */
const PrepackTemplatePicker = memo(function PrepackTemplatePicker({
  templates = [],
  value = null,
  onChange,
  pack,
  disabled = false,
  filterQuery = '',
  layout = 'strip',
}) {
  const grid = layout === 'grid'
  const selectedId = value ?? null
  const groupRef = useRef(null)
  const pickerId = useId()
  const groupName = `prepack-template-${pickerId}`
  const authoredTemplates = useMemo(() => templates.filter((template) => !template.isFallback), [templates])
  const previewBase = useMemo(() => (
    pack || {
      id: 'preview',
      title: 'Sample prepack',
      odds: '+250',
      legs: [
        { selection_name: 'Kansas City Chiefs', market_label: 'Moneyline', odds: '-110' },
        { selection_name: 'Over 47.5', market_label: 'Game Total', odds: '-110' },
      ],
    }
  ), [pack])
  // One stable pack per tile, so a tile's card only re-renders when the pack
  // or its template changes, not on every render of the builder.
  const tilePacks = useMemo(() => new Map(
    [null, ...authoredTemplates.map((template) => template.id)].map((templateId) => [templateId, { ...previewBase, template_id: templateId }]),
  ), [authoredTemplates, previewBase])

  // The carousel opens on the selected tile: a brand's default can be the
  // seventh card. Scrolls the strip (or the grid's box) only, never the page.
  useLayoutEffect(() => {
    const group = groupRef.current
    const checked = group?.querySelector('input:checked')?.closest('label')
    if (!group || !checked) return
    const groupRect = group.getBoundingClientRect()
    const tileRect = checked.getBoundingClientRect()
    if (grid) {
      if (tileRect.top < groupRect.top || tileRect.bottom > groupRect.bottom) group.scrollTop += tileRect.top - groupRect.top
    } else if (tileRect.left < groupRect.left || tileRect.right > groupRect.right) {
      group.scrollLeft += tileRect.left - groupRect.left
    }
    // Only on mount (a picker keeps its layout): later selections are made where the operator is looking.
  }, [grid])

  const query = String(filterQuery || '').trim().toLocaleLowerCase()
  // Tiles follow the Configuration gallery (#1043): the saved order, the Default
  // card at its saved slot, and a Default card deleted there (isHidden) is not
  // offered — unless it is what the pack uses: what is chosen is never hidden.
  // A catalog without a fallback row gets the Default card last, where
  // normalizeTemplates appends it.
  // A template that allows AI pictures says so: its packs and schedules can generate their own (#606). A builder
  // template without an image banner, and the Default card, say they have no picture (issue 683); artwork written by
  // hand may draw its own, so it says nothing.
  const tiles = templates.flatMap((template) => {
    if (template.isFallback) {
      if (template.isHidden && selectedId !== null) return []
      return [{ id: null, name: template.name || 'Default card', noImage: true }]
    }
    const image = bannerImageOf(template)
    const builderWithoutPicture = Boolean(template.builder) && !template.builder.code && !image
    return [{ id: template.id, name: template.name || 'Untitled template', aiPictures: Boolean(image?.ai_allowed), noImage: builderWithoutPicture }]
  })
  if (!templates.some((template) => template.isFallback)) tiles.push({ id: null, name: 'Default card', noImage: true })
  const matching = query ? tiles.filter((tile) => tile.name.toLocaleLowerCase().includes(query)) : tiles
  const selectedTile = tiles.find((tile) => tile.id === selectedId)
  // A filter never hides what is chosen: it is pinned first and marked.
  const pinned = query && selectedTile && !matching.includes(selectedTile) ? selectedTile : null
  const shown = pinned ? [pinned, ...matching] : matching

  const renderTile = (templateId, label, { marked = false, aiPictures = false, noImage = false } = {}) => {
    const selected = selectedId === templateId
    const radioId = `${pickerId}-radio-${templateId ?? 'default'}`
    const chipId = `${radioId}-ai`
    const noImageId = `${radioId}-no-image`
    return (
      <label
        key={templateId ?? 'native-default'}
        htmlFor={radioId}
        className={`group relative isolate flex cursor-pointer flex-col rounded-xl border transition-[border-color,background-color,box-shadow] outline-none focus-within:ring-2 focus-within:ring-[var(--prepacks-accent-text)] ${grid ? 'w-full min-w-0 p-1.5' : 'w-[240px] shrink-0 p-2'} ${
          selected
            ? 'border-[var(--prepacks-accent-text)] bg-[var(--wf-color-action-soft)] ring-1 ring-[var(--prepacks-accent-text)]'
            : 'border-[var(--prepacks-border)] bg-[var(--prepacks-surface)] hover:border-[var(--prepacks-muted)]'
        } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
      >
        {/* The name keeps the whole first row; the chips go below it, so a narrow tile still shows the name. */}
        <div className={`${grid ? 'mb-1.5' : 'mb-2'} px-1 pointer-events-none`}>
          <div className="flex items-center justify-between gap-2">
            <span className="min-w-0 truncate text-xs font-semibold text-[var(--wf-color-text)]">{label}</span>
            {selected ? (
              <span className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-[var(--prepacks-accent)] text-white" aria-hidden="true">
                <Check className="h-3 w-3 stroke-[3]" />
              </span>
            ) : null}
          </div>
          {aiPictures || noImage || marked ? (
            <div className="mt-1 flex flex-wrap gap-1">
              {aiPictures ? (
                <span id={chipId} className="rounded-full border border-[var(--prepacks-border)] px-2 text-[11px] font-medium text-[var(--prepacks-muted)]">AI pictures</span>
              ) : null}
              {noImage ? (
                <span id={noImageId} className="rounded-full border border-dashed border-[var(--prepacks-border)] px-2 text-[11px] font-medium text-[var(--prepacks-muted)]">No image</span>
              ) : null}
              {marked ? (
                <span className="rounded-full bg-[var(--wf-color-action-soft)] px-2 text-[11px] font-semibold text-[var(--wf-color-action-text)]">Selected</span>
              ) : null}
            </div>
          ) : null}
        </div>
        {/* A grid tile shows the card's top only: enough to tell the templates apart. */}
        <div aria-hidden="true" inert={true} className={`pointer-events-none w-full select-none ${grid ? 'max-h-28 overflow-hidden rounded-md' : ''}`}>
          <PrepackCard
            pack={tilePacks.get(templateId)}
            templates={templates}
            showActions={false}
            compact
          />
        </div>
        <input
          id={radioId}
          type="radio"
          name={groupName}
          value={templateId ?? ''}
          aria-label={label}
          aria-describedby={aiPictures ? chipId : noImage ? noImageId : undefined}
          checked={selected}
          disabled={disabled}
          onChange={() => onChange?.(templateId)}
          className="pointer-events-auto absolute inset-0 z-10 block h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
        />
      </label>
    )
  }

  return (
    <div className="w-full">
      <div
        ref={groupRef}
        role="radiogroup"
        aria-label="Card template"
        data-layout={layout}
        className={grid ? 'grid max-h-[22rem] grid-cols-[repeat(auto-fill,minmax(9.5rem,1fr))] gap-2 overflow-y-auto p-1' : 'flex gap-3 overflow-x-auto pb-3 pt-1'}
      >
        {shown.map((tile) => renderTile(tile.id, tile.name, { marked: tile === pinned, aiPictures: tile.aiPictures, noImage: tile.noImage }))}
      </div>
      {query && matching.length === 0 ? (
        <p className="m-0 text-xs text-[var(--wf-color-text-muted)]">{`No templates match “${String(filterQuery).trim()}”.`}</p>
      ) : null}
    </div>
  )
})

export default PrepackTemplatePicker
